import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { AlignLeft } from 'lucide-react'
import { TaskCheck } from '../TaskCheck'
import { TaskRewardTrigger } from '../TaskRewardTrigger'
import { SubtaskList } from '../SubtaskList'
import { useNestActions } from '../../hooks/useNestActions'
import { hasPendingSubtaskSelection } from '../../utils/pendingSubtaskSelection'
import { NestMenuContext } from '../../utils/nestMenu'
import { MultiSelectionContext } from '../../utils/multiSelection'
import { keepsScroll } from '../../utils/keepScroll'
import { MoveMenuContext } from '../../utils/moveMenu'
import { taskMenuHandlers } from '../TaskMenu/taskMenuHandlers'
import { MoveShortcutBox } from '../MoveMenu'
import { BranchProgress } from '../BranchProgress'
import { AutoHeight } from '@/src/renderer/components/ui/AutoHeight'
import { auditRender, renderAuditOn } from '@/src/renderer/lib/perf/renderAudit'
import { useSubtaskEditor } from '../../hooks/useSubtaskEditor'
import { MAX_SUBTASK_DEPTH } from '@/src/renderer/lib/db/taskTree'
import { resolveExpandedLayout } from '../../utils/resolveExpandedLayout'
import { isAtEdge, isField, stepField, stepForKey } from '../../utils/fieldChain'
import type { Tag } from '@/src/renderer/lib/db/models'
import { dateDiffDays } from '../../utils/parseStartDate'
import { getRewardById } from '@/src/renderer/lib/data/rewardCatalog'
import { useMidnightRefresh } from '../../hooks/useMidnightRefresh'
import { useTaskRowLayout } from '../../hooks/useTaskRowLayout'
import { useWindowMode } from '@/src/renderer/contexts/WindowModeProvider'
import { useTags } from '../../hooks/useTags'
import { TaskMenu } from '../TaskMenu'
import { TaskMenuShortcutBox } from '../TaskMenu/TaskMenuShortcutBox'
import type { StartValue } from '../WhenDropdown'
import type { DeadlineValue } from '../DeadlineDropdown'
import type { AttentionLevel, RecurrenceRule, Task } from '@/src/renderer/lib/db/models'
import { recurrenceTone, shortRecurrenceLabel } from '@/src/renderer/lib/recurrence'
import { recordRecent } from '@/src/renderer/lib/recent/recentItems'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import type { DurationValue } from './TaskEntry.types'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import type { TaskCreationData, TaskEntryProps } from './TaskEntry.types'
import { orderAtEndOf } from '../../utils/appendTaskOrders'
import { resolveTagNames } from '../../utils/resolveTagNames'
import { showCreatedElsewhereToast } from '../../utils/createdElsewhereToast'
import { useTaskEntryForm } from './hooks/useTaskEntryForm'
import { useTitleRecognition } from './hooks/useTitleRecognition'
import { useRecognizedFields } from './hooks/useRecognizedFields'
import { useTitleRecognitionPlace } from './TitleRecognitionScope'
import { TitleMarks } from './TitleMarks'
import { TitlePlaceholder } from './TitlePlaceholder'
import { TitleSuggestions } from './TitleSuggestions'
import { useTitleSuggestions } from './hooks/useTitleSuggestions'
import { Popover, PopoverAnchor, PopoverContent } from '@/src/renderer/components/ui/popover'
import type { TitleToken } from '@/src/renderer/lib/titleParser'
import { useCollapsedDropdowns } from './hooks/useCollapsedDropdowns'
import { useCollapsedRowFit } from './hooks/useCollapsedRowFit'
import { useContentHeight } from '@/src/renderer/hooks/useContentHeight'
import { useSubmitOnOutside } from './hooks/useSubmitOnOutside'
import { useAutosizeTitle } from './hooks/useAutosizeTitle'
import { dropdownLayerOpen } from './dropdownLayer'
import { CollapsedProjectBadge } from './CollapsedProjectBadge'
import { CollapsedRewardIcon } from './CollapsedRewardIcon'
import { useTabId } from '@/src/renderer/pages/TabStack/ActiveTabContext'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { CollapsedRecurrenceIcon } from './CollapsedRecurrenceIcon'
import { CollapsedTitleIcons } from './CollapsedTitleIcons'
import { CollapsedTimings } from './CollapsedTimings'
import { CollapsedTagPills, TagPillMeasurers } from './CollapsedTagPills'
import { TaskEntryPlaySlot } from './TaskEntryPlaySlot'
import { timestampTooltip } from './timestampTooltip'
import { TOOLTIP_DELAY, WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { ExpandedBottomBar } from './ExpandedBottomBar'
import { CommentMark } from '../CommentMark'
import { ReminderMark } from '../ReminderMark'
import { useOwnReminderLabels } from '../../hooks/useOwnReminderLabels'
import { useRowCommentCount } from '../TaskRowData'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useTaskDetailsOpener } from '../../hooks/TaskDetailsOpener'
import './TaskEntry.scss'
import { getScrollContainer } from '@/src/renderer/lib/dom/getScrollContainer'
import { DormantIcon } from '@/src/renderer/components/icons'

/**
 * TaskEntry — Unified component for task display and editing.
 *
 * Three modes that morph between each other:
 * - **collapsed**: Single row (TaskCheck + title)
 * - **expanded**: Editable card for editing existing tasks — replaces TaskCreationForm edit mode
 * - **creating**: Editable card for new tasks — replaces TaskCreationForm creation mode
 *
 * TaskCheck and title stay in the DOM across mode transitions.
 * Framer Motion `layout` animates their position changes smoothly (no fade).
 */
/** So lange gilt `--hull-open`: Tönung (70 + 520ms) und Karte (150 + 850ms) sind dann durch. */
const OPEN_TINT_MS = 1100
/** Höhe des Play-Knopfs in der zugeklappten Zeile (`TaskEntry.scss`, `--collapsed &__play-button`). */
const COLLAPSED_PLAY_HEIGHT = 24

const TaskEntryComponent: React.FC<TaskEntryProps> = ({
  mode,
  task,
  onToggleComplete,
  onDelete,
  onDuplicate,
  onContextMenuBadgeUpdate, getContextMenuMarks, menuShortcut, onMenuShortcutClose,
  onClick,
  onDoubleClick,
  onPlay,
  onSubmitCreate,
  deferNewTags = false,
  onSubmitEdit,
  onCancel,
  onSubmitCreateAndContinue,
  onSubmitCreateAndReveal,
  onSubmitEmpty,
  onSubmitEditAndCreateBelow,
  onNavigateNext,
  onNavigatePrev,
  onMorphComplete,
  selected = false,
  focused = false,
  multiSelected = false,
  selectionHull = false,
  openTintFrom,
  menuKind = 'task',
  onShowInProject,
  onReopen,
  projectBadgePlacement = 'title',
  titleAccessory,
  recurrenceDisplay = 'icon',
  hidePlay = false,
  checkKind = 'task',
  checkVariant = 'default',
  autoFocus = true,
  initialExpanded = false,
  resetKey,
  startDateLabelMode,
  inProjectContext = false,
  placement = 'list',
  actionsRef,
  onDraftChange,
}) => {
  /*
    **Wer rendert wie oft, und woran lag es** — nur im Dev-Betrieb und nur mit
    `localStorage.setItem('render-audit', '1')`. Gebraucht seit dem 21.09.2026:
    Nach dem Anlegen einer Aufgabe meldet `TabStack` Commits von über 200ms,
    und ohne diese Zeilen sieht man nicht, welche Prop die Liste in Bewegung
    setzt. Der Schlüssel ist ein Ref — er bleibt derselbe, solange die Zeile
    lebt.
  */
  const auditKey = useRef({})

  // **Zuletzt geöffnet** (#233): auch eine Aufgabe, die aufgeklappt in der Liste steht — nicht nur
  // eine mit offenen Details (gewünscht 02.10.2026). Das Anlegen (`creating`) zählt nicht.
  const openedTaskId = mode === 'expanded' ? task?.id : undefined
  useEffect(() => {
    if (openedTaskId) recordRecent({ kind: 'task', id: openedTaskId })
  }, [openedTaskId])

  const { t, i18n } = useTranslation()
  const { tags: allTags, createTag } = useTags()
  const dateKey = useMidnightRefresh()
  // Narrow compact window (list/stack): date badges render abbreviated labels
  // so the collapsed row (title + tags + badges) fits without overlap.
  const { isCompactMode } = useWindowMode()
  // Row layout mode ('single' | 'adaptive') — a layout preference,
  // deliberately separate from isCompactMode (see useTaskRowLayout).
  const { rowLayoutMode } = useTaskRowLayout()

  /*
    Gemessen wird ALLES, was die Zeile von außen bewegen kann — auch das, was
    nicht als Prop hereinkommt. Beim ersten Durchgang meldete das Protokoll
    `68 Renders — ohne Prop-Wechsel`, also 34 Zeilen ohne erkennbaren Grund:
    Die Ursache muss in den Hooks liegen (die Etiketten-Liste, die Uhr, die
    Fenstermasse) oder in einer Prop, die damals nicht beobachtet wurde.
  */
  if (renderAuditOn) auditRender('TaskEntry', auditKey.current, {
    task, selected, focused, multiSelected, menuShortcut, mode,
    onToggleComplete, onClick, onDoubleClick, onContextMenuBadgeUpdate,
    getContextMenuMarks, onPlay, onDelete, onDuplicate,
    onSubmitEdit, onSubmitEditAndCreateBelow, onCancel, onNavigateNext, onNavigatePrev,
    onShowInProject, onReopen, onMenuShortcutClose, onMorphComplete, onSubmitCreate,
    'hook:tags': allTags, 'hook:dateKey': dateKey,
    'hook:compact': isCompactMode, 'hook:rowLayout': rowLayoutMode, 'hook:t': t,
  })

  // Persisted for a saved task, buffered while creating — see useSubtaskEditor.
  // Sits above the form hook because the buffered drafts ride along in the
  // creation payload.
  const checklist = useSubtaskEditor({ taskId: task?.id, resetKey })
  /*
    ⇧⌥↑/↓ (#204): Unteraufgaben werden Aufgaben direkt über bzw. unter dieser. **Die bewegte
    Aufgabe bleibt angefasst** (gemeldet 27.09.2026): In der Liste ist sie danach ausgewählt,
    und die Karte schließt — über ihr eigenes Speichern, damit nichts Getipptes verloren geht.
    Erst dann greift ein weiteres ⇧⌥ in der Liste.
  */
  const { nest, unnest } = useNestActions()
  const nestMenu = useContext(NestMenuContext)
  const moveMenu = useContext(MoveMenuContext)
  // In einer Liste kommt die Mehrfachauswahl nicht als Prop (#303) — gefragt wird beim Öffnen des Kastens.
  const isMultiSelected = useContext(MultiSelectionContext)
  const submitRef = useRef<(() => void) | null>(null)
  const unnestSubtask = useCallback((ids: string[], where: 'above' | 'below') => {
    void unnest(ids, where).then(done => {
      if (!done || !nestMenu) return
      nestMenu.selectInList(ids)
      submitRef.current?.()
    })
  }, [unnest, nestMenu])
  // „Umhängen in …“ (#205): In der Liste folgt der Blick den Zeilen ins Ziel; sonst (Demnächst) gehen sie still.
  const moveSubtasks = useCallback((ids: string[], parentId: string) => {
    if (nestMenu) nestMenu.moveSubtasks(ids, parentId)
    else void nest(ids, parentId, { select: false })
  }, [nest, nestMenu])
  /*
    **Eine Unteraufgabe in Demnächst** (T14 #179) — erkennbar an ihrem Elternteil-Titel.
    Aufgeklappt trägt sie, was ihre Details tragen: eine eigene Checkliste bis Ebene 3 (#181),
    Belohnung und Wiederholung seit #205 — die Wiederholung nicht, wenn sich eine Ebene darüber
    wiederholt (`parentRecurring`), auch nicht in ihrer Checkliste.
  */
  const isSubtask = task?.parentTitle != null
  const hasChecklist = !isSubtask || (task?.depth ?? 1) < MAX_SUBTASK_DEPTH
  /*
    **Der Pfeil an einer Unteraufgabe** (T14 #179): Die Seite dieses Tabs wählt die Aufgabe
    und zeigt die Unteraufgabe in ihren Aufgaben-Details (`useTaskDetailsPane`). Beim
    Anlegen gibt es noch nichts zu öffnen.
  */
  const tabId = useTabId()
  const savedTaskId = mode !== 'creating' ? task?.id : undefined
  const openSubtask = useMemo(
    () => (savedTaskId ? (childId: string) => dispatchAppEvent('open-subtask', { tabId, parentId: savedTaskId, childId }) : undefined),
    [savedTaskId, tabId],
  )
  /*
    **Die Marke der Kommentare** (#183) öffnet die Aufgaben-Details auf „Kommentare" — nur auf
    Seiten, die welche haben; sonst ist sie Anzeige.
  */
  const detailsOpener = useTaskDetailsOpener()
  const commentCount = useRowCommentCount(savedTaskId)
  const openComments = useMemo(
    () => (savedTaskId && detailsOpener ? () => detailsOpener.openComments(savedTaskId) : undefined),
    [savedTaskId, detailsOpener],
  )
  const openSubtaskComments = useMemo(
    () => (savedTaskId && detailsOpener ? (childId: string) => detailsOpener.openComments(childId, savedTaskId) : undefined),
    [savedTaskId, detailsOpener],
  )
  // Die Glocke (Plan task-reminders): nur eigene Erinnerungen, öffnet die Details mit ihrem Chip.
  const reminderLabels = useOwnReminderLabels(savedTaskId)
  const openReminders = useMemo(
    () => (savedTaskId && detailsOpener ? () => detailsOpener.openReminders(savedTaskId) : undefined),
    [savedTaskId, detailsOpener],
  )
  const openSubtaskReminders = useMemo(
    () => (savedTaskId && detailsOpener ? (childId: string, tab?: 'at') => detailsOpener.openReminders(childId, savedTaskId, tab) : undefined),
    [savedTaskId, detailsOpener],
  )

  /*
    **Speichern geht erst durch die Erkennung** (#234): `enrichRef` säubert den Titel und setzt das Ziel
    (vor `isDirty`), `finishRef` legt neue Tags an und schreibt beim Bearbeiten Regel und Projekt. Die
    Erkennung steht weiter unten (sie braucht den Titel des Formulars), darum Refs, die jedes Render
    frisch setzt.
  */
  const enrichRef = useRef<((data: TaskCreationData) => TaskCreationData) | null>(null)
  const finishRef = useRef((data: TaskCreationData, done: (data: TaskCreationData) => void) => done(data))
  /** Läuft ein Speichern mit Schreiben vorweg (neuer Tag, Projekt aus dem Titel) noch, steht es hier. */
  const pendingFinishRef = useRef<Promise<void> | null>(null)
  const submitCreate = useCallback(
    (data: TaskCreationData) => finishRef.current(data, d => onSubmitCreate?.(d)),
    [onSubmitCreate],
  )
  const submitCreateAndContinue = useCallback(
    (data: TaskCreationData) => finishRef.current(data, d => onSubmitCreateAndContinue?.(d)),
    [onSubmitCreateAndContinue],
  )
  const submitCreateAndReveal = useCallback(
    (data: TaskCreationData) => finishRef.current(data, d => onSubmitCreateAndReveal?.(d)),
    [onSubmitCreateAndReveal],
  )
  const submitEdit = useCallback(
    (taskId: string, data: TaskCreationData) => finishRef.current(data, d => onSubmitEdit?.(taskId, d)),
    [onSubmitEdit],
  )
  const submitEditAndCreateBelow = useCallback(
    (taskId: string, data: TaskCreationData) => finishRef.current(data, d => onSubmitEditAndCreateBelow?.(taskId, d)),
    [onSubmitEditAndCreateBelow],
  )

  // Ein Ersteller kommt im Anlege-Modus zur Welt und klappt nach Enter als Platzhalter ein (#313, siehe `formFirst`).
  const [createdHere] = useState(() => mode === 'creating')

  // ── Form state + handlers (extracted hook) ──
  const form = useTaskEntryForm({
    mode, task, resetKey, selected, onClick, onCancel,
    onSubmitCreate: onSubmitCreate && submitCreate,
    onSubmitEdit: onSubmitEdit && submitEdit,
    onSubmitCreateAndContinue: onSubmitCreateAndContinue && submitCreateAndContinue,
    onSubmitCreateAndReveal: onSubmitCreateAndReveal && submitCreateAndReveal,
    onSubmitEmpty,
    onSubmitEditAndCreateBelow: onSubmitEditAndCreateBelow && submitEditAndCreateBelow,
    onContextMenuBadgeUpdate, subtaskDrafts: checklist.drafts, transformRef: enrichRef,
  })
  const {
    title, setTitle, notes, setNotes, checked, setChecked,
    attention, setAttention, duration, setDuration,
    tags, setTags, reward, start, setStart, deadline, setDeadline, recurrence, setRecurrence,
    handleAttentionChange, handleDurationChange, handleTagsChange, handleRewardChange,
    handleStartChange, handleDeadlineChange, handleRecurrenceChange,
    saveCollapsedToDb, selectOnDropdownOpen,
    buildData, handleSubmit, handleSubmitAndCreateNew, handleSubmitAndReveal, handleSubmitRef, optimisticTitleRef,
    playSoftRemove, isExpanded,
  } = form

  /*
    **Tab: speichern, dann zur Nachbarzeile.** Schreibt das Speichern erst noch (neuer Tag, Projekt aus dem
    Titel), geht es danach weiter — sonst klappte das späte Speichern die eben geöffnete Zeile wieder zu.
  */
  const submitAndNavigate = useCallback((back: boolean) => {
    handleSubmit()
    const go = () => (back ? onNavigatePrev?.() : onNavigateNext?.())
    const pending = pendingFinishRef.current
    if (pending) void pending.then(go)
    else go()
  }, [handleSubmit, onNavigatePrev, onNavigateNext])

  /*
    Ein Ersteller in der Liste sperrt Tab — der Fokus soll die Zeile nicht verlassen (`1abcb9a8`). Die
    Schnelleingabe hat unter dem Feld eine eigene Leiste mit echten Knöpfen; dort führt Tab hin (#278).
  */
  const blocksTab = mode === 'creating' && placement !== 'quick-add'

  // ── Erkennung im Titel (#234): beim Anlegen (B1) und Bearbeiten (B2) — nicht an Unteraufgaben ──
  const recognitionPlace = useTitleRecognitionPlace()
  const recognitionOn = recognitionPlace != null && (mode === 'creating' || (mode === 'expanded' && !isSubtask))
  // Mit Erkennung zeigen wechselnde Beispiele aus dem Bereich der Seite den Platzhalter (#258) — als Überlagerung
  // hinter dem Feld (`TitlePlaceholder`), weil sich ein nativer nicht überblenden lässt. Das Feld bleibt dann leer.
  const showTitleExamples = recognitionOn && recognitionPlace != null && title === ''
  const recognition = useTitleRecognition({
    place: recognitionPlace, enabled: recognitionOn, title, language: i18n.language, dateKey, tags: allTags,
    // Beim Bearbeiten gilt, was schon im Titel stand, als Text gelassen (Entscheidung 4).
    protectedText: mode === 'expanded' ? task?.title ?? null : null,
    resetSignal: `${mode}:${resetKey ?? 0}`,
  })
  const { parsed, target: recognizedTarget, dismiss: dismissToken } = recognition
  const recognized = useRecognizedFields({
    parsed,
    values: { start, deadline, duration, attention, recurrence, tags },
    setters: { setStart, setDeadline, setDuration, setAttention, setRecurrence, setTags },
    dismiss: dismissToken,
    resetSignal: `${mode}:${resetKey ?? 0}`,
  })
  const pendingTagNames = useMemo(
    () => parsed.fields.tags.flatMap(tag => (tag.id ? [] : [tag.name])),
    [parsed.fields.tags],
  )

  /*
    **Vorschläge nach `#`, `/`, `@`** (Schritt C) — nur, solange getippt wird. Der Caret zählt erst, wenn
    er vom Tippen kommt: Wer eine Zeile mit „#Garten“ am Ende aufklappt oder in ein altes Wort klickt,
    soll keine Liste aufspringen sehen.
  */
  const source = useDataSource()
  const [suggestCaret, setSuggestCaret] = useState<number | null>(null)
  const typedCaretRef = useRef<number | null>(null)
  const suggestions = useTitleSuggestions({
    enabled: recognitionOn, title, caret: suggestCaret, place: recognitionPlace, catalog: recognition.catalog, parsed,
  })
  const pendingCaretRef = useRef<number | null>(null)
  const { choose, addSection } = recognition
  const acceptSuggestion = useCallback((index?: number) => {
    const result = suggestions.accept(index)
    const item = suggestions.items[index ?? suggestions.highlighted]
    if (!result || !item || !suggestions.trigger) return
    const start = suggestions.trigger.start
    const end = start + 1 + item.name.length
    // Ausdrücklich gewählt — bei zwei Projekten „Empty“ gilt dieses, nicht das der Seite.
    if (item.type === 'project' || item.type === 'section') choose(result.text, { kind: item.type, id: item.id, start, end })
    // Ein neuer Abschnitt entsteht sofort (am Ende des Projekts) und gilt dann als gewählt.
    if (item.type === 'new-section') {
      void source.createSection({ projectId: item.projectId, title: item.name }).then(section => {
        addSection(section)
        choose(result.text, { kind: 'section', id: section.id, start, end })
      })
    }
    pendingCaretRef.current = result.caret
    typedCaretRef.current = null
    setSuggestCaret(null)
    setTitle(result.text)
  }, [suggestions, setTitle, choose, addSection, source])
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    typedCaretRef.current = e.target.selectionStart
    setSuggestCaret(e.target.selectionStart)
    setTitle(e.target.value)
  }, [setTitle])
  const handleTitleSelect = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const { selectionStart, selectionEnd } = e.currentTarget
    if (selectionStart === selectionEnd && selectionStart === typedCaretRef.current) return
    typedCaretRef.current = null
    setSuggestCaret(null)
  }, [])
  const handleTitleBlur = useCallback(() => {
    typedCaretRef.current = null
    setSuggestCaret(null)
  }, [])

  const enrichCreation = useCallback((data: TaskCreationData): TaskCreationData => {
    if (!recognitionOn || parsed.tokens.length === 0) return data
    return {
      ...data,
      // Nur Erkanntes im Titel („morgen 30min“) — dann bleibt der Text, sonst gäbe es keinen Titel.
      title: parsed.title || data.title,
      target: recognizedTarget
        ? {
            projectId: recognizedTarget.projectId, sectionId: recognizedTarget.sectionId,
            lifeSpace: recognizedTarget.lifeSpace, projectName: recognizedTarget.projectName,
          }
        : undefined,
      // Nur ein Tag schlägt in Demnächst den Tag der Gruppe — „9 Uhr“ allein gilt an deren Tag.
      startFromTitle: parsed.fields.startDated === true,
      newTagNames: pendingTagNames.length ? pendingTagNames : undefined,
    }
  }, [recognitionOn, parsed, recognizedTarget, pendingTagNames])

  enrichRef.current = enrichCreation

  /*
    **Beim Bearbeiten schreibt die Zeile selbst, was das Speichern der Seite nicht kennt** (B2): die
    Regel aus dem Titel und das Projekt (`#Steuer /Belege` verschiebt ans Ende des Abschnitts, wie
    „Verschieben“). Wann, Dauer & Co. trägt `onSubmitEdit` wie sonst.
  */
  const writeRecognizedEdit = async (current: NonNullable<typeof task>, data: TaskCreationData) => {
    const patch: Partial<Omit<Task, 'id'>> = {}
    if (JSON.stringify(data.recurrence ?? null) !== JSON.stringify(current.recurrence ?? null)) patch.recurrence = data.recurrence ?? null
    const { target } = data
    const moves = target && (target.projectId !== current.projectId || (target.sectionId != null && target.sectionId !== current.sectionId))
    if (target && moves) {
      Object.assign(patch, {
        projectId: target.projectId, sectionId: target.sectionId, lifeSpace: target.lifeSpace,
        order: await orderAtEndOf(source, target.projectId, target.sectionId),
      })
    }
    if (Object.keys(patch).length === 0) return
    // Direkt an diese Aufgabe — der Kanal der Seite (`saveToDb`) träfe die ganze Mehrfachauswahl.
    await source.updateTask(current.id, patch)
    if (target && target.projectId !== current.projectId) {
      const moved = await source.getTask(current.id)
      if (moved) showCreatedElsewhereToast(t, target.projectName || t('project.defaultName'), moved, 'moved')
    }
  }

  finishRef.current = (data, done) => {
    if (!recognitionOn) { done(data); return }
    const editing = mode === 'expanded' && task ? task : null
    const needsWrite = editing != null && (data.target != null
      || JSON.stringify(data.recurrence ?? null) !== JSON.stringify(editing.recurrence ?? null))
    // Speichert der Host erst später, legt er die neuen Tags dann selbst an (`newTagNames`, #241).
    const tagNames = deferNewTags && !editing ? [] : pendingTagNames
    if (tagNames.length === 0 && !needsWrite) { done(data); return }
    pendingFinishRef.current = (async () => {
      // Neue Tags (`@büro`) entstehen erst jetzt.
      const ids = await resolveTagNames(tagNames, allTags, createTag)
      const withTags = ids.length ? { ...data, tags: [...new Set([...(data.tags ?? []), ...ids])] } : data
      if (editing) await writeRecognizedEdit(editing, withTags)
      done(withTags)
    })().finally(() => { pendingFinishRef.current = null })
  }

  const onDraftChangeRef = useRef(onDraftChange)
  onDraftChangeRef.current = onDraftChange
  useEffect(() => {
    if (mode === 'creating') onDraftChangeRef.current?.(buildData())
  }, [mode, buildData, enrichCreation])

  // Ein Chip von Hand macht das erkannte Wort zu Text (Entscheidung 12).
  const onStartByHand = useCallback((value: StartValue | null) => { recognized.onManualChange('start'); handleStartChange(value) }, [recognized, handleStartChange])
  const onDeadlineByHand = useCallback((value: DeadlineValue | null) => { recognized.onManualChange('deadline'); handleDeadlineChange(value) }, [recognized, handleDeadlineChange])
  const onDurationByHand = useCallback((value: DurationValue | null) => { recognized.onManualChange('duration'); handleDurationChange(value) }, [recognized, handleDurationChange])
  const onAttentionByHand = useCallback((value: AttentionLevel | null) => { recognized.onManualChange('attention'); handleAttentionChange(value) }, [recognized, handleAttentionChange])
  const onRecurrenceByHand = useCallback((rule: RecurrenceRule | null) => { recognized.onManualChange('recurrence'); handleRecurrenceChange(rule) }, [recognized, handleRecurrenceChange])
  const onTagsByHand = useCallback((value: string[]) => { recognized.onManualTags(value); handleTagsChange(value) }, [recognized, handleTagsChange])
  const dismissTargetWords = useCallback(() => {
    dismissToken(parsed.tokens.filter(token => token.kind === 'project' || token.kind === 'section'))
  }, [dismissToken, parsed.tokens])
  const dismissPendingTag = useCallback((name: string) => {
    dismissToken(parsed.tokens.filter(token => token.kind === 'tag' && token.text.slice(1).toLowerCase() === name.toLowerCase()))
  }, [dismissToken, parsed.tokens])

  // ── Refs ──
  const entryRef = useRef<HTMLDivElement>(null)
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  const notesInputRef = useRef<HTMLTextAreaElement>(null)

  // Nach dem Übernehmen eines Vorschlags steht der Caret hinter dem Namen.
  useLayoutEffect(() => {
    const at = pendingCaretRef.current
    const input = titleInputRef.current
    if (at == null || !input) return
    pendingCaretRef.current = null
    input.setSelectionRange(at, at)
  }, [title])

  // Die Liste hängt am Zeichen in der Textkopie — dort steht es genau so wie im Feld.
  const suggestAnchorRef = useRef<HTMLSpanElement>(null)
  const suggestVirtualRef = useRef({
    getBoundingClientRect: () => suggestAnchorRef.current?.getBoundingClientRect()
      ?? titleInputRef.current?.getBoundingClientRect() ?? new DOMRect(),
  })

  // ── Collapsed dropdown states ──
  const dropdowns = useCollapsedDropdowns(isExpanded, entryRef)
  const [isRowHovered, setIsRowHovered] = useState(false)

  // ── Right-click context menu state ──
  // Lifted so the TaskMenu can imperatively open the same dropdown
  // that the trailing 3-dots Reactivate button shows.
  const [reactivateOpen, setReactivateOpen] = useState(false)

  // Used by both the direct-wake button (in-project) and the context-
  // menu's "Aufwecken" entry. Prefers the host's optimistic-update sink
  // so the row flips out of dormant state on next paint instead of
  // after the full IDB → queueSyncChange → LiveQuery roundtrip — same
  // path the right-click badge updates take.
  const handleDirectWake = useCallback(() => {
    if (!task) return
    // Eine wiederkehrende Aufgabe wacht am nächsten Termin auf, nicht überfällig.
    const wake = wakeTaskUpdate(task, new Date())
    if (onContextMenuBadgeUpdate) {
      onContextMenuBadgeUpdate(task.id, wake)
    } else {
      void form.saveCollapsedToDb(wake)
    }
  }, [task, onContextMenuBadgeUpdate, form])

  const prevModeRef = useRef(mode)
  const justCollapsedRef = useRef(false)

  // ── Title textarea autosize (expanded) ──
  useAutosizeTitle(titleInputRef, isExpanded, title)

  // ── Expanded content area (notes / subtasks / reward) ──
  const [notesFocused, setNotesFocused] = useState(false)
  const [rewardTriggerOpen, setRewardTriggerOpen] = useState(false)

  const expandedLayout = resolveExpandedLayout({
    hasNotes: notes.trim().length > 0,
    hasSubtasks: checklist.items.length > 0,
    hasReward: reward != null,
  })
  // The icon is the empty-state affordance. It leaves on focus rather than on
  // the first keystroke so the 24px shift reads as entering the field, not as
  // the text jumping while typing.
  const showNotesIcon = expandedLayout.notes === 'placeholder' && !notesFocused

  const handleRewardTriggerChange = useCallback((value: string | null) => {
    if (value === null) playSoftRemove()
    handleRewardChange(value)
  }, [handleRewardChange, playSoftRemove])

  // ── Auto-focus title input ──
  useEffect(() => {
    if (!isExpanded || !autoFocus) return
    const timer = setTimeout(() => {
      const el = titleInputRef.current
      if (!el) return
      /*
        **Eingehängt: der Fokus gehört der neuen Unteraufgabe** (#204, gemeldet 27.09.2026). Die
        Karte klappt auf, weil etwas in ihr gelandet ist — nicht, um ihren Titel zu schreiben.
        Nähme sie ihn sich, wäre die eben bewegte Zeile nicht mehr angefasst.
      */
      if (hasPendingSubtaskSelection() || document.activeElement?.closest('.subtask-list')) return
      el.focus({ preventScroll: true })
      const len = el.value.length
      el.setSelectionRange(len, len)
    }, 50)
    return () => clearTimeout(timer)
  }, [isExpanded, autoFocus])

  // ── Morph transition detection ──
  useEffect(() => {
    const prevMode = prevModeRef.current
    prevModeRef.current = mode
    if ((prevMode === 'creating' || prevMode === 'expanded') && mode === 'collapsed') {
      justCollapsedRef.current = true
      const timer = setTimeout(() => {
        justCollapsedRef.current = false
        onMorphComplete?.()
      }, 350)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [mode, onMorphComplete])

  // ── Scroll focused item into view (collapsed mode) ──
  useEffect(() => {
    if (focused && entryRef.current && !justCollapsedRef.current) {
      const el = entryRef.current
      const container = getScrollContainer(el)
      // ⌘A legt den Fokus ans Ende, der Blick bleibt (#303, `keepScroll`).
      if (container && keepsScroll(container)) return
      if (container) {
        const smooth = container.hasAttribute('data-smooth-scroll')
        if (smooth) container.removeAttribute('data-smooth-scroll')
        const elRect = el.getBoundingClientRect()
        const cRect = container.getBoundingClientRect()
        if (elRect.top < cRect.top || elRect.bottom > cRect.bottom) {
          el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'nearest' })
        }
      } else {
        el.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [focused])

  // ── Click outside / document-level Enter → submit (expanded/creating) ──
  useSubmitOnOutside(entryRef, isExpanded && placement !== 'quick-add', handleSubmitRef)
  submitRef.current = isExpanded ? handleSubmit : null
  if (actionsRef) {
    actionsRef.current = {
      submit: handleSubmit,
      submitAndContinue: handleSubmitAndCreateNew,
      focusTitle: () => {
        const input = titleInputRef.current
        if (!input) return
        input.focus()
        input.setSelectionRange(input.value.length, input.value.length)
      },
    }
  }

  // ── Keyboard handlers ──

  /**
   * **↑ und ↓ gehen ein Schreibfeld weiter** — Titel, Notiz, die Zeilen der
   * Checkliste, zuletzt die **offene Zeile** (Nutzer-Vorgabe, 2026-09-20).
   *
   * Dieselbe Kette wie in den **Aufgaben-Details** (`utils/fieldChain`); dort
   * gab es sie seit dem 19.09., hier endete der Weg bisher bei der Notiz. Die
   * Notiz lässt die Taste erst an ihrer Kante los, sonst käme man mit den
   * Pfeilen nicht mehr durch drei Absätze.
   */
  const handleFieldArrow = useCallback((e: React.KeyboardEvent): boolean => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return false
    const field = e.target
    if (!isField(field)) return false
    const step = stepForKey(e.key)
    if (step === null) return false
    if (!isAtEdge(field, step) || !stepField(entryRef.current, field, step)) return false
    e.preventDefault()
    e.stopPropagation()
    return true
  }, [])

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Offene Vorschläge zuerst: ↑/↓ wählen, Enter/Tab übernehmen, Esc schließt nur die Liste.
    if (suggestions.open && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // Steht der Name schon ganz da („#Test“), gibt es nichts zu ergänzen — Enter speichert gleich. Gibt es
      // ihn zweimal (Persönlich › Empty, Arbeit › Empty) und wurde eine andere Zeile gewählt als die, die die
      // Erkennung ohnehin nimmt, übernimmt Enter erst: Dann zählt die Wahl.
      // Ein neuer Abschnitt will angelegt werden — dafür ist Enter da, auch wenn sein Name schon dasteht.
      const current = suggestions.items[suggestions.highlighted]
      const name = current?.name.toLowerCase()
      const twice = suggestions.items.filter(item => item.name.toLowerCase() === name).length > 1
      const complete = suggestions.trigger != null && current?.type !== 'new-section'
        && name === suggestions.trigger.query.toLowerCase()
        && (!twice || suggestions.highlighted === suggestions.defaultHighlighted)
      if (complete && e.key === 'Enter') suggestions.close()
      const handled =
        e.key === 'ArrowDown' ? (suggestions.move(1), true)
        : e.key === 'ArrowUp' ? (suggestions.move(-1), true)
        // Passt nichts („Kein Projekt …“), speichert Enter wie sonst — das Wort bleibt Text.
        : ((e.key === 'Enter' && !complete) || e.key === 'Tab') && !e.shiftKey && suggestions.items.length > 0 ? (acceptSuggestion(), true)
        : e.key === 'Escape' ? (suggestions.close(), true)
        : false
      if (handled) { e.preventDefault(); e.stopPropagation(); return }
    }
    if (handleFieldArrow(e)) return
    // Rücktaste direkt hinter einem erkannten Wort nimmt erst die Markierung, dann Buchstaben.
    if (e.key === 'Backspace' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const { selectionStart, selectionEnd } = e.currentTarget
      const token = selectionStart === selectionEnd ? parsed.tokens.find(t => t.end === selectionStart) : undefined
      if (token) { e.preventDefault(); dismissToken(token); return }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmitAndCreateNew(); return }
    if (e.key === 'Enter' && e.shiftKey && !e.altKey && onSubmitCreateAndReveal && mode === 'creating') {
      e.preventDefault()
      handleSubmitAndReveal()
      return
    }
    if (e.key === 'Enter') { e.preventDefault(); handleSubmit() }
    if (e.key === 'Tab' && mode === 'expanded') { e.preventDefault(); submitAndNavigate(e.shiftKey); return }
    if (e.key === 'Tab' && blocksTab) { e.preventDefault(); return }
    if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
  }, [handleFieldArrow, handleSubmit, handleSubmitAndCreateNew, handleSubmitAndReveal, onSubmitCreateAndReveal, onCancel, submitAndNavigate, mode, blocksTab, parsed.tokens, dismissToken, suggestions, acceptSuggestion])

  /*
    **Als Text lassen per Klick** (#234). Welches Wort gemeint ist, sagt die Lage des Zeigers über den
    Markierungen (`getClientRects` der Kopie) — der Caret allein träfe am ersten Buchstaben den Rand.
    Ohne Maße (jsdom) zählt der Caret, wenn er IM Wort steht. Ein als Text gelassenes Wort markiert
    der Klick wieder.
  */
  const marksRef = useRef<HTMLDivElement>(null)
  const [hoveredMark, setHoveredMark] = useState<number | null>(null)
  const markAt = useCallback((x: number, y: number): number | null => {
    for (const mark of marksRef.current?.querySelectorAll<HTMLElement>('mark') ?? []) {
      for (const rect of mark.getClientRects()) {
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return Number(mark.dataset.start)
      }
    }
    return null
  }, [])
  const handleTitleMouseMove = useCallback((e: React.MouseEvent) => {
    if (parsed.tokens.length === 0) return
    const start = markAt(e.clientX, e.clientY)
    setHoveredMark(prev => (prev === start ? prev : start))
  }, [parsed.tokens.length, markAt])
  const handleTitleMouseLeave = useCallback(() => setHoveredMark(null), [])
  const handleTitleClick = useCallback((e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (!recognitionOn) return
    const { selectionStart, selectionEnd } = e.currentTarget
    if (selectionStart !== selectionEnd) return
    const hit = markAt(e.clientX, e.clientY)
    const token = (hit != null ? parsed.tokens.find(t => t.start === hit) : undefined) ?? recognition.tokenInside(selectionStart)
    if (token) {
      dismissToken(token)
      setHoveredMark(null)
      return
    }
    const suppressed = recognition.suppressedInside(selectionStart)
    if (suppressed) recognition.activate(suppressed)
  }, [recognitionOn, markAt, parsed.tokens, recognition, dismissToken])
  const overMark = recognitionOn && hoveredMark != null && parsed.tokens.some(t => t.start === hoveredMark)
  // Der Tooltip „Als Text lassen“ kommt mit der üblichen Wartezeit; die Hand sofort.
  const [hintMark, setHintMark] = useState<number | null>(null)
  useEffect(() => {
    if (!overMark) { setHintMark(null); return }
    const timer = setTimeout(() => setHintMark(hoveredMark), TOOLTIP_DELAY)
    return () => clearTimeout(timer)
  }, [overMark, hoveredMark])

  const handleNotesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (handleFieldArrow(e)) return
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmitAndCreateNew(); return }
    // Shift+Enter belongs to the textarea — it is the only way to put a hard
    // line break into the notes now that they wrap.
    if (e.key === 'Enter' && e.shiftKey) { e.stopPropagation(); return }
    if (e.key === 'Enter') { e.preventDefault(); handleSubmit() }
    if (e.key === 'Tab' && mode === 'expanded') { e.preventDefault(); submitAndNavigate(e.shiftKey); return }
    if (e.key === 'Tab' && blocksTab) { e.preventDefault(); return }
    if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
  }, [handleFieldArrow, handleSubmit, handleSubmitAndCreateNew, onCancel, submitAndNavigate, mode, blocksTab])

  const handleContainerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isExpanded) return
    /*
      **⌘D in der offenen Karte** (#209, Sven 28.09.2026): erst speichern und schließen, dann die
      Kopie. Offen geblieben, stand die neue Aufgabe ausgewählt da, als hätte sie den Fokus — der
      Caret steckte aber noch im Titel der alten. Angefasste Unteraufgaben fangen ⌘D vorher ab.
    */
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === 'd' && task && onDuplicate) {
      if (dropdownLayerOpen()) return
      e.preventDefault()
      e.stopPropagation()
      handleSubmit()
      // Ein Bild später: Dann trägt die Liste den gespeicherten Stand, und die Kopie mit ihm.
      const id = task.id
      requestAnimationFrame(() => onDuplicate(id))
      return
    }
    /*
      **Escape schließt die Karte auch außerhalb von Titel und Notiz** (gemeldet 04.10.2026): An einer
      angefassten Unteraufgabe kam die Taste hier an und verpuffte. Wer sie schon verbraucht hat
      (Titel, Notiz, eine Zeile beim Schreiben, ein Radix-Menü), hat `preventDefault` gesetzt oder
      sie angehalten. Ein beschreibbares Feld behält sie — darin wird geschrieben.
    */
    if (e.key === 'Escape') {
      if (e.defaultPrevented || dropdownLayerOpen()) return
      const target = e.target as HTMLElement
      const writable = (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) && !target.readOnly
      if (writable || target.isContentEditable) return
      e.preventDefault()
      onCancel?.()
      return
    }
    if (e.key !== 'Enter') return
    const target = e.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
    if (dropdownLayerOpen()) return
    e.preventDefault()
    handleSubmit()
  }, [isExpanded, handleSubmit, task, onDuplicate, onCancel])

  // ── Collapsed mode handlers ──
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (task) onClick?.(task.id, e)
  }, [onClick, task])

  const handleDoubleClickEvent = useCallback(() => {
    if (task) onDoubleClick?.(task.id)
  }, [onDoubleClick, task])

  /*
    Lage und Öffnen gehören seit dem **Aufgaben-Menü** Radix; hier bleibt nur,
    was der Rechtsklick sonst noch bedeutet: Eine noch nicht ausgewählte Aufgabe
    wählt er aus. Ohne das meinte das Menü eine andere Aufgabe als die, auf die
    man gezeigt hat.

    Kein `preventDefault` mehr — der Trigger von Radix braucht das Ereignis.
  */
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (isExpanded) return
    if (!selected && onClick && task) onClick(task.id, e)
  }, [isExpanded, selected, onClick, task])

  /*
    **Gerade abgewählt und noch unter dem Zeiger: kein Hover, bis er die Zeile verlässt** (#293).
    Sonst stand sofort die weiße Hover-Pille da, während die Hülle noch zurückwich (Prototyp, Fix 2).
    Im Rendern abgeleitet und nicht im Effekt: Die Pille stünde sonst ein Bild lang da.
  */
  const [calm, setCalm] = useState(false)
  const [wasSelected, setWasSelected] = useState(selected)
  if (wasSelected !== selected) {
    setWasSelected(selected)
    setCalm(selectionHull && !selected && isRowHovered)
  }

  /*
    **Aufklappen aus der Markierung bleibt flächig** (#293, Sven 09.10.2026: „immens wichtig“). Früher
    trug die markierte Zeile ihre Tönung selbst; beim Aufgehen fiel `--selected` weg und `--expanded`
    blendete sie in 0,5s aus, während die Karte aufging. In einer Hüllen-Liste ist die Zeile
    durchsichtig — geht sie markiert auf, holt sie sich die Tönung für diesen Übergang zurück
    (`--hull-open`). Gemerkt wird, ob sie zugeklappt markiert WAR: Im Bild des Aufgehens zählt der
    Zustand davor.
  */
  const collapsedSelected = !isExpanded && selected
  /*
    Eine Eingabe aus dem Zug von „Neue Aufgabe“ (#301) entsteht schon offen, ohne Übergang davor:
    Sie startet getönt (`openTintFrom`). Karte und Rumpf sind dann vom ersten Bild an weiß, ihre
    Übergänge greifen nicht — `born` gibt ihnen dieselben Zeiten als Keyframes.
  */
  const [openTint, setOpenTint] = useState(() => ({
    expanded: isExpanded,
    collapsedSelected,
    tint: (isExpanded && openTintFrom ? openTintFrom : null) as 'selected' | 'dormant' | null,
    born: isExpanded && Boolean(openTintFrom),
  }))
  if (openTint.expanded !== isExpanded || (!isExpanded && openTint.collapsedSelected !== collapsedSelected)) {
    const opening = isExpanded && !openTint.expanded && selectionHull && openTint.collapsedSelected
    setOpenTint({
      expanded: isExpanded,
      collapsedSelected: isExpanded ? openTint.collapsedSelected : collapsedSelected,
      tint: opening ? (task?.dormant ? 'dormant' : 'selected') : isExpanded ? openTint.tint : null,
      born: false,
    })
  }
  // Nach dem Übergang (Karte deckt bis 1s) fällt die Klasse wieder weg: Bliebe sie, startete die
  // Animation nach einem Tab-Wechsel neu — ein Vorfahr mit `display: none` setzt sie zurück.
  useEffect(() => {
    if (!openTint.tint) return
    const timer = setTimeout(() => setOpenTint(prev => ({ ...prev, tint: null, born: false })), OPEN_TINT_MS)
    return () => clearTimeout(timer)
  }, [openTint.tint])

  const handleRowMouseEnter = useCallback(() => setIsRowHovered(true), [])
  const handleRowMouseLeave = useCallback(() => {
    setIsRowHovered(false)
    setCalm(false)
  }, [])
  const handleCardContextMenu = useCallback((e: React.MouseEvent) => e.stopPropagation(), [])
  const handleCheckChange = useCallback((val: boolean) => {
    // Auch aufgeklappt speichert der Haken sofort, wie zugeklappt — über denselben Weg
    // (Kopplung der Unteraufgaben, Wiederholung, Geschafft-Moment, ⌘Z). Als bloßer
    // Formular-Stand ging er beim Zuklappen verloren. Der Formular-Stand zeigt ihn,
    // bis die Datenquelle nachzieht.
    if (isExpanded) setChecked(val)
    if (task) onToggleComplete?.(task.id)
    // Abgehakt klappt die Karte zu und speichert dabei, was geändert war: Die Aufgabe
    // landet geschlossen unter Geschafft (Nutzer, 27.09.2026). Der Stapel gibt kein
    // `onCancel` mit — seine Karte bleibt offen.
    if (isExpanded && val) handleSubmit()
  }, [isExpanded, setChecked, task, onToggleComplete, handleSubmit])

  // ── Computed values ──
  const displayTitle = isExpanded ? title : (optimisticTitleRef.current ?? task?.title ?? '')
  const isChecked = isExpanded ? checked : (task?.completed ?? false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [dateKey])
  // Zugeklappt zählt nur die Aufgabe (#313): Das Formular bekommt beim Einzel-Setzen (Karte, Marke, Menü)
  // einen Wert und wird erst beim Aufklappen wieder aus ihr gelesen. Schrieb ein Weg an der Zeile vorbei
  // (Listen-Pille, Sync, Rückgängig), blieb sonst der alte Wert stehen.
  // Ausnahme: der Ersteller, der nach Enter als Platzhalter einklappt — seine Aufgabe ist eine Attrappe
  // ohne Werte, was gewählt wurde, steht nur im Formular. Ein Ersteller wird nie zur echten Zeile.
  const formFirst = isExpanded || createdHere
  const effectiveAttention = (formFirst ? attention ?? task?.attention : task?.attention) ?? null
  const effectiveStartDate = (formFirst ? start?.date ?? task?.startDate : task?.startDate) ?? null
  const startDiff = effectiveStartDate ? dateDiffDays(effectiveStartDate, now) : null
  const isStartToday = startDiff === 0
  const isStartTomorrow = startDiff === 1
  const deadlineDiff = deadline ? dateDiffDays(deadline.date, now) : null
  const isDeadlineToday = deadlineDiff === 0
  const isDeadlineTomorrow = deadlineDiff === 1

  // Die Markierung im Titel trägt den Ton ihres Chips (gewünscht 03.10.2026) — die Farbe sagt etwas.
  const recognizedRule = parsed.fields.recurrence
  const recognizedAttention = parsed.fields.attention
  const markTone = useCallback((token: TitleToken): string | undefined => {
    switch (token.kind) {
      case 'deadline': return deadlineDiff === null ? undefined : deadlineDiff < 0 ? 'overdue' : deadlineDiff === 0 ? 'urgent' : undefined
      case 'when': return startDiff !== null && startDiff < 0 ? 'urgent' : undefined
      case 'recurrence': return recognizedRule ? `recurrence-${recurrenceTone(recognizedRule)}` : undefined
      case 'attention': return recognizedAttention ? `attention-${recognizedAttention}` : undefined
      default: return undefined
    }
  }, [deadlineDiff, startDiff, recognizedRule, recognizedAttention])

  const collapsedDueDate = task?.dueDate
  const collapsedDueTime = task?.dueTime
  const collapsedDuePeriod = task?.duePeriod
  const collapsedDeadlineValue = useMemo(
    () => !isExpanded && collapsedDueDate
      ? { date: collapsedDueDate, time: collapsedDueTime ?? null, period: collapsedDuePeriod ?? null }
      : null,
    [isExpanded, collapsedDueDate, collapsedDueTime, collapsedDuePeriod],
  )
  const collapsedDeadlineDiff = collapsedDeadlineValue ? dateDiffDays(collapsedDeadlineValue.date, now) : null
  const isCollapsedDeadlineToday = collapsedDeadlineDiff === 0
  const isCollapsedDeadlineTomorrow = collapsedDeadlineDiff === 1
  const isCollapsedDeadlineOverdue = collapsedDeadlineDiff !== null && collapsedDeadlineDiff < 0

  const collapsedTagObjects = useMemo((): Tag[] => {
    if (!task?.tags?.length) return []
    return task.tags.map(id => allTags.find(t => t.id === id)).filter((t): t is Tag => !!t)
  }, [task?.tags, allTags])

  // Mehrfachauswahl (#303): Rendert die Zeile ohnehin, fragt sie die Liste — der Play-Platz wird dann gar nicht gebaut.
  // Zeilen, die beim Wechsel nicht rendern, blendet `.task-list--multi-selected` aus.
  const showCollapsedPlay = !hidePlay && !isExpanded && !multiSelected && !isMultiSelected?.() && (
    isRowHovered || selected || dropdowns.anyOpen
  )

  // Track expand↔collapse transitions for play button crossfade
  const wasExpandedRef = useRef(isExpanded)
  const collapsingFromExpanded = wasExpandedRef.current && !isExpanded
  const expandingFromCollapsed = !wasExpandedRef.current && isExpanded
  useEffect(() => { wasExpandedRef.current = isExpanded }, [isExpanded])

  // Keep ExpandedBottomBar mounted for the full duration of the close
  // animation. Without this, `{!isExpanded ? null : ...}` would rip the bar
  // out of the DOM the instant isExpanded flips to false — the outer
  // motion.div would then animate opacity/y/height on an empty wrapper, and
  // the user sees "content vanishes → empty box collapses" instead of
  // "badges slide up and fade, then the container closes".
  //
  // Must be derived synchronously in render — a useEffect-only flag would
  // be one render stale on the open transition (isExpanded true but flag
  // still false) and the bar would skip its fade-in / slide-up entirely.
  //
  // `collapsingFromExpanded` (computed above) is true during the ONE render
  // where isExpanded flips to false — that's enough to keep the bar mounted
  // while `collapseActive` (set in useEffect) catches up for the remaining
  // frames of the animation.
  const [collapseActive, setCollapseActive] = useState(false)
  useEffect(() => {
    if (isExpanded) {
      setCollapseActive(false)
      return
    }
    setCollapseActive(true)
    // Matches the bottom-wrapper close duration (~0.25 s + cushion).
    const timer = setTimeout(() => setCollapseActive(false), 280)
    return () => clearTimeout(timer)
  }, [isExpanded])
  const renderExpandedBottom = isExpanded || collapsingFromExpanded || collapseActive

  /*
    **Zugehend bleibt Play oben rechts** (Sven, 09.10.2026). Zugeklappt steht er mittig
    (`align-self: center`) — galt das schon im ersten Bild des Zuklappens, sprang er in die Mitte der
    noch offenen Karte und fuhr mit ihr hoch. Solange die Karte zugeht, trägt die Zeile `--closing`,
    und sie schließt sich um ihn. Nur beim echten Zuklappen, nicht beim Einhängen einer Zeile.
  */
  const [closing, setClosing] = useState(false)
  const [wasOpen, setWasOpen] = useState(isExpanded)
  if (wasOpen !== isExpanded) {
    setWasOpen(isExpanded)
    setClosing(!isExpanded)
  }
  useEffect(() => {
    if (!closing) return
    // Wie das Zugehen von Karte und Leiste (0,25s) samt Polster, siehe `collapseActive`.
    const timer = setTimeout(() => setClosing(false), 280)
    return () => clearTimeout(timer)
  }, [closing])

  const collapsedStartValue = isExpanded ? null
    : createdHere && start ? start
    : task?.startDate ? { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null }
    : null

  const anyDropdownOpen = dropdowns.anyOpen

  // ── Re-sync hover state with DOM :hover ──
  // Native mouseenter/mouseleave can miss transitions when an overlaying
  // dropdown portal (backdrop) was placed above the row during badge
  // interaction. Once the task is no longer selected and no dropdown is open,
  // verify the hover state against the actual pointer position so the play
  // button doesn't linger when the mouse is no longer over the row.
  useEffect(() => {
    if (isExpanded || anyDropdownOpen) return
    if (selected || !isRowHovered) return
    const actuallyHovered = !!entryRef.current?.matches(':hover')
    if (!actuallyHovered) {
      setIsRowHovered(false)
      // `calm` wartet auf ein `mouseleave`, das dann nie kommt (#293).
      setCalm(false)
    }
  }, [selected, anyDropdownOpen, isExpanded, isRowHovered])

  // Adaptive row layout: measure whether title + all badges fit on one
  // line (classic layout). Only when they don't, the row breaks into two
  // lines: row 1 = title only, row 2 (__meta) = all property badges.
  // Only applies while collapsed — expanded/creating keep their own
  // layout. Rows without any property always stay single-line.
  const adaptiveRow = rowLayoutMode === 'adaptive' && !isExpanded
  // Die Wiederholungs-Marke steht rechts vor Ende und Dauer; allein dort trägt sie ihren Rhythmus (#283).
  const recurrenceShort = task?.recurrence ? shortRecurrenceLabel(task.recurrence, t, i18n.language) : ''
  const recurrenceAlone = !!task?.recurrence && !collapsedDeadlineValue && !task?.estimatedDuration
  const rowFitKey = [
    displayTitle,
    commentCount,
    effectiveAttention ?? '',
    startDiff ?? '',
    collapsedStartValue?.period ?? '',
    collapsedTagObjects.map((tag) => tag.name).join(','),
    task?.estimatedDuration ?? '',
    task?.durationMode ?? '',
    collapsedDeadlineValue
      ? `${collapsedDeadlineValue.date.getTime()}-${collapsedDeadlineValue.time}-${collapsedDeadlineValue.period}`
      : '',
    task?.projectName ?? '',
    task?.rewardId ?? '',
    // The checklist progress behind the title arrives with the checklist and widens with it —
    // `__main` keeps its size, so the ResizeObserver alone would never re-measure.
    `${checklist.progress.done}/${checklist.progress.total}`,
    // The recurrence mark sits in the timings cluster; alone it carries its rhythm text.
    task?.recurrence ? `${recurrenceShort}${recurrenceAlone ? '+' : ''}` : '',
    isCompactMode,
    dateKey,
  ].join('|')
  const { fits: rowFits, summarizeTags, recurrenceText: recurrenceTextFits } = useCollapsedRowFit(entryRef, adaptiveRow, rowFitKey)
  // Zu wenig Platz: Der Text fällt zuerst weg, der Titel behält seinen Platz (#283).
  const recurrenceWithText = recurrenceAlone && (!adaptiveRow || recurrenceTextFits !== false)
  // Attention/start always stay in the title row, so row 2 only ever holds
  // tags/duration/deadline — without any of those, breaking into two lines
  // gains nothing and the row stays single-line (title just ellipsizes).
  const hasCollapsedMeta = !!(
    collapsedTagObjects.length > 0 ||
    task?.estimatedDuration ||
    collapsedDeadlineValue
  )
  const twoLine = adaptiveRow && !rowFits && hasCollapsedMeta

  /*
    Wo Play beim Zugehen steht (`--closing`): in der Mitte der zugeklappten Zeile. Gemessen wird ihr
    Inhalt ohne die zugehende Karte (`__extra`) — einzeilig 2px, zweizeilig mehr. Feste 2px trafen nur die
    einzeilige Zeile, zweizeilig rückte Play am Ende um gut 12px nach (Diff-Audit 3).
  */
  useLayoutEffect(() => {
    const el = entryRef.current
    if (!el) return
    if (!closing) {
      el.style.removeProperty('--closing-play-top')
      return
    }
    const content = el.querySelector<HTMLElement>('.task-entry__content')
    if (!content) return
    const extra = content.querySelector<HTMLElement>('.task-entry__extra')
    const rowHeight = content.getBoundingClientRect().height - (extra?.getBoundingClientRect().height ?? 0)
    el.style.setProperty('--closing-play-top', `${Math.max(0, (rowHeight - COLLAPSED_PLAY_HEIGHT) / 2)}px`)
  }, [closing, twoLine])

  // Tag presentation — measured, not window-mode-bound: in adaptive mode
  // all tags show individually while they fit; when space runs out they
  // collapse into ONE summary pill ("Work +2"). Non-adaptive keeps the
  // classic rule: up to 3 pills, then 2 + a separate "+N" overflow pill.
  const summarizeCollapsedTags = adaptiveRow && summarizeTags && collapsedTagObjects.length > 1
  const collapsedTagsVisible = summarizeCollapsedTags
    ? collapsedTagObjects.slice(0, 1)
    : adaptiveRow || collapsedTagObjects.length <= 3
      ? collapsedTagObjects
      : collapsedTagObjects.slice(0, 2)
  const collapsedTagsOverflow = summarizeCollapsedTags
    ? collapsedTagObjects.length - 1
    : !adaptiveRow && collapsedTagObjects.length > 3
      ? collapsedTagObjects.length - 2
      : 0

  /*
    **Gemessen wird nur, was schon einmal offen stand.** `AutoHeight` hängt an
    seinem Inhalt einen `ResizeObserver` — einer je Aufgabenzeile wäre in einer
    langen Liste Verschwendung, und der Titel einer zusammengeklappten Zeile
    ändert seine Höhe nie von selbst.

    Der Merker bleibt gesetzt, wenn die Zeile wieder zugeht: Der Rückweg soll
    genauso fahren wie der Hinweg.
  */
  const [everExpanded, setEverExpanded] = useState(isExpanded)
  /*
    **Der Merker muss schon in DEM Bild stehen, in dem die Zeile aufgeht.** Als Effekt
    kam er ein Bild zu spät: Beim ERSTEN Aufklappen stand die Hülle noch auf `auto`,
    der Titel sprang in einem Zug von einer auf zwei Zeilen, und erst danach fing das
    Messen an — die Bewegung fiel ganz aus. Ab dem zweiten Mal lief sie sauber, weil
    der Merker dann schon gesetzt war (gemessen am 21.09.2026: 28 → 48 in einem Bild
    beim ersten Mal, 28 → 48 über 270ms beim zweiten).

    Zustand während des Renderns anzupassen ist genau dafür da: React rendert sofort
    noch einmal, bevor etwas gemalt wird.
  */
  if (isExpanded && !everExpanded) setEverExpanded(true)

  /*
    **Der aufgeklappte Teil fährt auf eine GEMESSENE Höhe, nicht auf `auto`.**

    Framer misst ein `height: 'auto'` EINMAL beim Start der Bewegung und setzt am
    Ende die echte Höhe. Stimmen die beiden nicht überein, schnappt die Karte im
    letzten Bild — dieselbe Falle wie seinerzeit bei der Ziel-Karte.

    Genau das passierte bei einer Zeile mit **Zeitmarken** (Dauer, Deadline):
    Beim Öffnen wandern sie aus der Zeile in die untere Leiste, und die stand im
    Moment der Messung noch eine Zeile höher. Gemessen am 22.09.2026 in der App:
    Die Karte fuhr weich auf 266 und fiel in EINEM Bild auf 224 zurück — 42
    Pixel. Ohne Zeitmarken gab es den Unterschied nicht, darum hüpften nur
    manche Zeilen (Nutzer: „wenn vor dem Play-Knopf was hängt, dann hüpft es").

    Mit dem Maß folgt das Ziel dem Inhalt: Der Beobachter meldet die neue Höhe,
    und die Bewegung fährt sie an, statt sie zu setzen. Ohne Maß (jsdom, Tab
    hinter `display: none`) bleibt es bei `auto` — kein Übergang, aber auch
    nichts Geklemmtes.
  */
  // Die Ruhe-Höhe nur, wo die Karte gleich aufgehen kann: unter dem Zeiger (Doppelklick), fokussiert oder als einzige
  // ausgewählt (Enter; Demnächst kennt nur die Auswahl). Bei ⌘A erzwang der Blick sonst in jeder Zeile ein Layout (#303).
  const mayOpenSoon = isRowHovered || focused || (selected && !multiSelected && !isMultiSelected?.())
  const [extraBodyRef, extraHeight] = useContentHeight(isExpanded, mayOpenSoon)


  const className = [
    'task-entry',
    isExpanded ? 'task-entry--expanded' : 'task-entry--collapsed',
    twoLine && 'task-entry--two-line',
    checklist.items.length > 0 && 'task-entry--has-subtasks',
    !isExpanded && task?.completed && 'task-entry--completed',
    !isExpanded && task?.dormant && 'task-entry--dormant',
    !isExpanded && selected && 'task-entry--selected',
    !isExpanded && focused && !selected && 'task-entry--focused',
    selectionHull && 'task-entry--hull',
    // Gedrückt färbt sich eine Zeile nur ohne Hülle; dort fährt die Markierung erst beim Klick hin (#293).
    !isExpanded && !selectionHull && 'task-entry--pressable',
    isExpanded && openTint.tint === 'selected' && 'task-entry--hull-open',
    isExpanded && openTint.tint === 'dormant' && 'task-entry--hull-open-dormant',
    isExpanded && openTint.tint && openTint.born && 'task-entry--hull-open-born',
    !isExpanded && calm && !selected && 'task-entry--calm',
    !isExpanded && closing && 'task-entry--closing',
    anyDropdownOpen && 'task-entry--dropdown-open',
    placement === 'quick-add' && 'task-entry--quick-add',
  ].filter(Boolean).join(' ')

  /*
    Was die Inhalte des **Aufgaben-Menüs** zurückmelden — einmal gebündelt,
    weil ZWEI Wege sie brauchen: das aufgeklappte Unter-Menü im Rechtsklick-Menü
    und der Kasten, den ein **Tastenkürzel** ohne Menü öffnet.

    Der Weg hinaus ist derselbe wie beim Rechtsklick: der optimistische Kanal
    des Aufrufers, der die Änderung auf die ganze AUSWAHL anwendet
    (`useContextMenuBadgeUpdate`), sonst der Direktschreiber der Zeile.
  */
  const applyMenuUpdate = useCallback((updates: Parameters<typeof saveCollapsedToDb>[0]) => {
    if (!task) return
    if (onContextMenuBadgeUpdate) onContextMenuBadgeUpdate(task.id, updates)
    else saveCollapsedToDb(updates)
  }, [task, onContextMenuBadgeUpdate, saveCollapsedToDb])

  /**
   * **„Ruhen lassen"** — EIN Griff für alle drei Wege an der Zeile: die Zeile im
   * **Aufgaben-Menü**, die Zeile im Wann-Kasten dahinter und die im Wann-Kasten
   * der **Marke**. Über denselben Kanal wie jede Menü-Änderung, also auf die
   * ganze AUSWAHL (CONTEXT.md, gemischte Auswahl).
   */
  const handleRest = useCallback(() => {
    applyMenuUpdate({ pausedAt: new Date() })
    showSuccessToast({ message: t('taskItem.pausedToast'), description: t('taskItem.pausedToastText', { count: 1 }), icon: <DormantIcon size={18} />, tone: 'rest' })
  }, [applyMenuUpdate, t])

  /**
   * **Favorit** (#210) — über denselben Kanal, also auf die ganze Auswahl. Neu favorisiert landet
   * unten in „Favoriten“: die Zeit in ms ist größer als jede frühere Stelle.
   */
  const handleFavoriteChange = useCallback((favorite: boolean) => {
    applyMenuUpdate({ favoriteOrder: favorite ? Date.now() : null })
  }, [applyMenuUpdate])

  // ── Badge clusters ──
  // Built once, rendered either inline in the title row / next to the play
  // button (single-line layout) or collected in the __meta row below the
  // title (two-line layout) — same elements, same dropdown wiring.
  const titleIconsNode = (
    <CollapsedTitleIcons
      isExpanded={isExpanded}
      selected={selected}
      effectiveAttention={effectiveAttention}
      effectiveStartDate={effectiveStartDate}
      startDiff={startDiff}
      collapsedStartValue={collapsedStartValue}
      taskTitle={displayTitle}
      locale={i18n.language}
      dropdowns={dropdowns}
      setAttention={setAttention}
      setStart={setStart}
      saveCollapsedToDb={saveCollapsedToDb}
      selectOnDropdownOpen={selectOnDropdownOpen}
      playSoftRemove={playSoftRemove}
      startDateLabelMode={startDateLabelMode}
      compact={isCompactMode}
      // Eine ruhende Aufgabe hat nichts mehr, das schlafen könnte.
      onRest={task && !task.dormant ? handleRest : undefined}
    />
  )

  const tagsNode = (
    <CollapsedTagPills
      visibleTags={collapsedTagsVisible}
      overflowCount={collapsedTagsOverflow}
      summarize={summarizeCollapsedTags}
      taskTags={collapsedTagObjects}
      selectedIds={task?.tags ?? []}
      show={!isExpanded && collapsedTagsVisible.length > 0}
      dropdowns={dropdowns}
      selectOnDropdownOpen={selectOnDropdownOpen}
      saveTags={(value) => saveCollapsedToDb({ tags: value })}
      playSoftRemove={playSoftRemove}
    />
  )

  // Built once, placed either inside the first content row or as its own block
  // below — same element either way, so it keeps its identity (and any focus)
  // when a growing checklist or a filled notes paragraph pushes it down.
  /*
    **Eine einzige Stelle im Baum** (21.09.2026). Der Knoten stand bis dahin an
    zwei: in der ersten Reihe neben den Notizen, oder darunter. Dasselbe React-
    Element an zwei Positionen behält aber **nicht** seine Identität — beim
    ersten Eintrag wechselte das Layout, und die ganze Liste wurde neu gebaut.
    Der Caret fiel dabei auf `body`, und die Enter-Kette riss nach der
    ersten Zeile ab (gefunden im E2E-Lauf).

    Wohin er gehört, sagt jetzt eine Klasse; den Umbruch macht das Flexbox-Wrap
    der Reihe (`TaskEntry.scss`).
  */
  const subtaskNode = (
    <div
      className={`task-entry__subtasks task-entry__subtasks--${
        expandedLayout.subtasks === 'placeholder-inline' ? 'inline' : 'below'
      }`}
      onKeyDown={handleFieldArrow}
    >
      <SubtaskList
      // Ein neues Anlegen beginnt mit leerer Liste — auch die angefangene offene Zeile (Bild 47, 04.10.2026).
      key={mode === 'creating' ? `reset-${resetKey ?? 0}` : undefined}
      items={checklist.items}
      onAdd={checklist.add}
      onInsertAfter={checklist.insertAfter}
      onMove={checklist.move}
      onToggle={checklist.toggle}
      coupledSpark={isExpanded}
      parentId={task?.id}
      onUnnest={unnestSubtask}
      onUpdate={savedTaskId ? checklist.update : undefined}
      onDuplicate={savedTaskId ? checklist.duplicate : undefined}
      onIndent={savedTaskId ? checklist.indent : undefined}
      onOutdent={savedTaskId ? checklist.outdent : undefined}
      onMoveTo={moveSubtasks}
      parentRecurring={recurrence != null || !!task?.parentRecurring}
      onRename={checklist.rename}
      onRemove={checklist.remove}
      // Ohne Rückweg kein „Unteraufgabe gelöscht“-Toast: Im kleinen Fenster der Schnelleingabe stapelte er sich
      // über die Karte (Bild 45, 04.10.2026).
      onRestore={placement === 'quick-add' ? undefined : checklist.restore}
      onOpen={openSubtask}
      onOpenComments={openSubtaskComments}
      onOpenReminders={openSubtaskReminders}
      />
    </div>
  )

  const menuHandlers = useMemo(
    () => taskMenuHandlers(applyMenuUpdate, { setStart, setDuration, setAttention, setRecurrence }),
    [applyMenuUpdate, setStart, setDuration, setAttention, setRecurrence],
  )

  const timingsNode = (
    <AnimatePresence>
    {!isExpanded && task && (
      <motion.div
        className="task-entry__timings"
        data-row-fit="timings"
        initial={{ opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        /*
          **Beim Hinausfahren aus dem Fluss.** Die Gruppe ist ein Flex-Kind von
          `__main` und teilt sich die Zeile mit der Titel-Spalte. Blieb sie
          während ihrer 0,1 s im Fluss, fehlten dem aufgehenden Teil darunter
          83 Pixel Breite — die Notizen brachen eine Zeile mehr um, und wenn
          die Gruppe dann verschwand, sprangen sie zurück. Das war der Rest des
          gemeldeten Hüpfens: ein Wippen, das es nur mit Dauer oder Deadline
          gab (Nutzer, 22.09.2026).

          `position: absolute` ohne Abstände lässt sie genau dort stehen, wo sie
          stand (ihre statische Lage) — sie fährt also unverändert hinaus und
          nimmt nur die Breite nicht mehr mit.
        */
        exit={{ opacity: 0, y: 16, position: 'absolute' }}
        transition={{ duration: 0.1, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Erstes Glied, links neben Ende und Dauer (#283): wie oft, bis wann, wie lange. */}
        <CollapsedRecurrenceIcon
          isExpanded={isExpanded}
          recurrence={task.recurrence}
          seedDate={task.startDate}
          dropdowns={dropdowns}
          onRecurrenceChange={menuHandlers.onRecurrenceChange}
          selectOnDropdownOpen={selectOnDropdownOpen}
          playSoftRemove={playSoftRemove}
          display={recurrenceDisplay}
          withText={recurrenceWithText}
        />
        <CollapsedTimings
          task={task}
          collapsedDeadlineValue={collapsedDeadlineValue}
          isCollapsedDeadlineToday={isCollapsedDeadlineToday}
          isCollapsedDeadlineTomorrow={isCollapsedDeadlineTomorrow}
          isCollapsedDeadlineOverdue={isCollapsedDeadlineOverdue}
          locale={i18n.language}
          compact={isCompactMode}
          dropdowns={dropdowns}
          setDuration={setDuration}
          saveCollapsedToDb={saveCollapsedToDb}
          selectOnDropdownOpen={selectOnDropdownOpen}
          playSoftRemove={playSoftRemove}
        />
      </motion.div>
    )}
    </AnimatePresence>
  )

  // Gemeint ist immer genau eine Zeile — die, deren Kennung mitkam.
  // Unter einer wiederkehrenden Aufgabe hat eine Unteraufgabe keinen eigenen Rhythmus (#205).
  // Verschieben (#257) nur, wo die Seite es anbietet — und nie an einer Unteraufgabe (sie hat „Umhängen“).
  const menuShortcutTarget = menuShortcut && task && menuShortcut.taskId === task.id
    && !(menuShortcut.target === 'recurrence' && task.parentRecurring)
    && !(menuShortcut.target === 'move' && (!moveMenu || isSubtask))
    ? menuShortcut.target
    : null

  /*
    **Welche Marken die zusammengeklappte Zeile gerade trägt.**

    Dieselben Bedingungen, unter denen `CollapsedTitleIcons`, `CollapsedTimings`,
    `CollapsedTagPills` und `CollapsedRewardIcon` ihre Knöpfe zeigen — hier
    einmal gebündelt, weil das Kürzel wissen muss, ob es etwas zum Drücken
    gibt. Läuft eine der Bedingungen dort auseinander, zeigt das Kürzel auf
    einen Knopf, den es nicht gibt, und der Kasten bliebe zu.
  */
  const collapsedMarks = useMemo(() => ({
    start: !!effectiveStartDate && startDiff !== null,
    attention: !!effectiveAttention,
    duration: !!task?.estimatedDuration,
    deadline: !!collapsedDeadlineValue,
    tags: collapsedTagsVisible.length > 0,
    // `CollapsedRewardIcon` zeigt nichts, wenn die Kennung im Katalog fehlt —
    // etwa nach einem App-Update, das eine Belohnung zurückgezogen hat.
    reward: !!getRewardById(task?.rewardId),
    recurrence: !!task?.recurrence,
    // Verschieben hat keine Marke — es geht immer am Anker auf.
    move: false,
  }), [effectiveStartDate, startDiff, effectiveAttention, task?.estimatedDuration,
       collapsedDeadlineValue, collapsedTagsVisible.length, task?.rewardId, task?.recurrence])

  /*
    **Ein Kürzel drückt die Marke, wenn es sie gibt.**

    `W` auf einer Aufgabe mit Startdatum soll dasselbe tun wie der Klick auf
    ihr „Mo" — gleiche Lage, gleicher Inhalt. Und der Inhalt ist wirklich ein
    anderer: Die Fassung am Knopf (`placement="badge"`) bringt den Papierkorb zum
    Entfernen mit, die im Menü nicht.

    Ohne Marke bleibt es beim **Anker** unten an der Zeile (siehe dort) — das
    ist der häufige Fall, und dafür wurde er gebaut. Aufgeklappt gibt es keine
    Marken; dann gilt ebenfalls der Anker.

    Entschieden wird BEIM RENDERN und nicht in einem Effekt: Stünde der Anker
    für ein Bild da, spränge der Kasten sichtbar von unten links zur Marke.
  */
  const shortcutMarkTarget = menuShortcutTarget && !isExpanded && collapsedMarks[menuShortcutTarget]
    ? menuShortcutTarget
    : null
  const shortcutAnchorTarget = menuShortcutTarget && !shortcutMarkTarget ? menuShortcutTarget : null

  // Über Refs, damit der Effekt unten an EINER Sache hängt — dem Ziel. Die
  // Setzer kommen aus `useState` und sind stabil; `dropdowns` als Ganzes ist
  // bei jedem Bild ein neues Objekt und taugte nicht als Abhängigkeit.
  const markOpenersRef = useRef(dropdowns)
  markOpenersRef.current = dropdowns
  const firstTagIdRef = useRef<string | null>(null)
  firstTagIdRef.current = collapsedTagsVisible[0]?.id ?? null
  const selectOnDropdownOpenRef = useRef(selectOnDropdownOpen)
  selectOnDropdownOpenRef.current = selectOnDropdownOpen
  const menuShortcutCloseRef = useRef(onMenuShortcutClose)
  menuShortcutCloseRef.current = onMenuShortcutClose

  useEffect(() => {
    if (!shortcutMarkTarget) return
    const d = markOpenersRef.current
    // Die Tags teilen sich EIN Dropdown; `activeTagId` sagt, welche Pille es
    // gerade hält. Ohne diese Zeile wüsste keine von sich, dass sie gemeint ist.
    if (shortcutMarkTarget === 'tags') d.setActiveTagId(firstTagIdRef.current)
    const open = {
      start: d.setStartOpen,
      attention: d.setAttentionOpen,
      duration: d.setDurationOpen,
      deadline: d.setDeadlineOpen,
      tags: d.setTagsOpen,
      reward: d.setRewardOpen,
      recurrence: d.setRecurrenceOpen,
    }[shortcutMarkTarget]
    open(true)
    // Was der Klick auf den Knopf auch täte — er läuft über `onOpenChange`,
    // hier wird der Zustand direkt gesetzt.
    selectOnDropdownOpenRef.current(true)
    // Das Kürzel ist verbraucht: Der Kasten gehört jetzt dem Knopf und schließt
    // über dessen `onOpenChange`.
    menuShortcutCloseRef.current?.()
  }, [shortcutMarkTarget])

  // ── JSX ──
  const entry = (
    <div
      className={className}
      ref={entryRef}
      // „Entdecken“ (#323) zeigt Fokus und Belohnung an der ersten Zeile.
      data-intro-anchor={isExpanded ? undefined : 'task'}
      onClick={!isExpanded ? handleClick : undefined}
      onDoubleClick={!isExpanded ? handleDoubleClickEvent : undefined}
      onContextMenu={!isExpanded ? handleContextMenu : undefined}
      onKeyDown={handleContainerKeyDown}
      onMouseEnter={!isExpanded ? handleRowMouseEnter : undefined}
      onMouseLeave={!isExpanded ? handleRowMouseLeave : undefined}
      role={!isExpanded && onClick ? 'button' : undefined}
      tabIndex={-1}
    >
      {/*
        **Der Anker für die Tastenkürzel des Aufgaben-Menüs.**

        Ein Kürzel wie `W` öffnet dieselbe Sache wie die Menüzeile — nur ohne
        Menü, und dann braucht der Kasten etwas, woran er hängt. Trägt die Zeile
        die **Marke** schon, drückt das Kürzel sie (siehe `shortcutMarkTarget`
        oben); dieser Anker ist für den anderen, häufigeren Fall: Die Marke
        steht nur da, solange die Eigenschaft GESETZT ist.

        Also ein eigener Punkt, unsichtbar und ohne Maß, unten links an der
        Zeile — dort, wo auch die Marken ihre Kästen aufziehen. Er ist immer da;
        ein Anker, der erst mit dem Kürzel entstünde, hätte im ersten Bild noch
        keine Lage.
      */}
      {/*
        **Der Kasten gehört nicht der Zeile.** Er liegt im DOM in einem Portal,
        im React-Baum aber in dieser Zeile — ein Klick darin liefe zu ihrem
        `onClick` hoch. Das wählte die Aufgabe neu, und das native Auswahlfeld
        des Wiederkehr-Kastens verlor den Fokus und klappte sofort wieder zu
        (gemeldet 2026-09-24, ⇧W → „Nie"). Die Marken schirmen ihre Kästen
        genauso ab (`CollapsedRecurrenceIcon`).
      */}
      {shortcutAnchorTarget === 'move' && task && moveMenu && (
        <MoveShortcutBox
          taskTitle={task.title || t('taskList.nest.untitled')}
          countMoving={() => moveMenu.countMoving(task.id)}
          startSpace={task.lifeSpace ?? 'personal'}
          current={multiSelected || isMultiSelected?.() ? null : { projectId: task.projectId ?? null, sectionId: task.sectionId ?? null }}
          onSelect={(target, place) => moveMenu.move(task.id, target, place)}
          onCreateProject={(project) => moveMenu.moveToNewProject(task.id, project)}
          onClose={() => onMenuShortcutClose?.()}
        />
      )}
      {shortcutAnchorTarget && shortcutAnchorTarget !== 'move' && task && (
        <TaskMenuShortcutBox
          target={shortcutAnchorTarget}
          task={task}
          handlers={menuHandlers}
          onRest={task.dormant ? undefined : handleRest}
          // Das Kürzel schreibt auf die ganze Auswahl — Entfernen folgt ihr (#313).
          removable={getContextMenuMarks?.(task.id).removable}
          onClose={() => onMenuShortcutClose?.()}
          anchor="row"
        />
      )}
      <div
        className="task-entry__card"
        onContextMenu={isExpanded ? handleCardContextMenu : undefined}
      >
        <div className="task-entry__main" data-row-fit="main">
          <div className="task-entry__check" data-row-fit="check">
            <TaskCheck
              checked={isChecked}
              onCheckedChange={handleCheckChange}
              // Auch eine wiederkehrende Aufgabe trägt den normalen Kreis — „kommt wieder"
              // sagt allein die Marke hinter dem Titel (CONTEXT.md, Wiederkehrend; 2026-09-13).
              kind={checkKind}
              variant={checkVariant}
              disabled={mode === 'creating'}
              active={isExpanded}
              dormant={task?.dormant ?? false}
              /* Trägt die Aufgabe eine Belohnung, folgt gleich der
                 Geschafft-Moment mit eigenem Klang — dann schweigt der Haken.
                 Bei einer Mehrfachauswahl kommt der Moment zwar nicht, aber
                 dort tickt ohnehin jede Zeile für sich; ein Ton weniger fällt
                 weniger auf als zwei übereinander. */
              silent={!!task?.rewardId}
            />
          </div>

          <div className="task-entry__content">
            {/*
              **Der Titel wechselt die Zeilenzahl, die Höhe fährt mit.**
              Zusammengeklappt steht er einzeilig mit Ellipsis, aufgeklappt als
              Textfeld über mehrere Zeilen — ohne Messung spränge die Karte
              beim Aufklappen um die Differenz. `height: auto` löst dafür
              keinen Übergang aus; `AutoHeight` misst und fährt in Pixeln
              (dieselbe Regel wie überall sonst in der App).

              Ohne Maße — inaktives Tab hinter `display: none`, jsdom — bleibt
              es bei `auto`: kein Übergang, aber auch nichts Abgeschnittenes.
            */}
            <AutoHeight
              animated={everExpanded}
              watchRest={mayOpenSoon}
              // Dieselbe Zeit wie der aufgehende Rumpf (`__extra`, 0,25s):
              // Titel und Karte wachsen als EINE Bewegung. Vorher hielt eine
              // Fessel den Titel erst einzeilig und ließ ihn danach nachwachsen
              // — die Karte wuchs zweimal (Nutzer, 21.09.2026).
              duration={0.25}
              bodyClassName="task-entry__title-area"
            >
              {titleIconsNode}
              {isExpanded ? (
                <div className="task-entry__title-field">
                  {recognitionOn && (
                    <TitleMarks
                      ref={marksRef}
                      className="task-entry__title-marks"
                      text={title}
                      tokens={parsed.tokens}
                      hintAt={hintMark}
                      hint={t('titleRecognition.dismiss')}
                      toneOf={markTone}
                      anchorAt={suggestions.open ? suggestions.trigger?.start : null}
                      anchorRef={suggestAnchorRef}
                    />
                  )}
                  {recognitionOn && (
                    <Popover open={suggestions.open} onOpenChange={(open) => { if (!open) suggestions.close() }}>
                      <PopoverAnchor virtualRef={suggestVirtualRef} />
                      {suggestions.open && suggestions.trigger && (
                        <PopoverContent
                          side="bottom"
                          align="start"
                          collisionPadding={{ top: 56, bottom: 8, left: 8, right: 8 }}
                          className="context-menu-content title-suggestions-panel"
                          // Der Fokus bleibt im Titel — dort wird weitergetippt.
                          onOpenAutoFocus={(e) => e.preventDefault()}
                          onCloseAutoFocus={(e) => e.preventDefault()}
                          onInteractOutside={(e) => { if (e.target === titleInputRef.current) e.preventDefault() }}
                        >
                          <TitleSuggestions
                            kind={suggestions.trigger.kind}
                            query={suggestions.trigger.query}
                            groups={suggestions.groups}
                            highlighted={suggestions.highlighted}
                            onHighlight={suggestions.setHighlighted}
                            onAccept={acceptSuggestion}
                          />
                        </PopoverContent>
                      )}
                    </Popover>
                  )}
                  {showTitleExamples && recognitionPlace && <TitlePlaceholder lifeSpace={recognitionPlace.lifeSpace} />}
                  <textarea
                    ref={titleInputRef}
                    className={`task-entry__title-input${title ? ' task-entry__title-input--filled' : ''}${overMark ? ' task-entry__title-input--over-mark' : ''}`}
                    // Die Beispiele sind `aria-hidden` — den Namen trägt das Label, nicht der Platzhalter.
                    aria-label={t('taskCreation.titleLabel')}
                    placeholder={recognitionOn ? '' : t('taskCreation.titlePlaceholder')}
                    value={title}
                    rows={1}
                    onChange={handleTitleChange}
                    onSelect={handleTitleSelect}
                    onBlur={handleTitleBlur}
                    onKeyDown={handleTitleKeyDown}
                    onClick={handleTitleClick}
                    onMouseMove={handleTitleMouseMove}
                    onMouseLeave={handleTitleMouseLeave}
                    spellCheck={false}
                  />
                </div>
              ) : (
                <span className="task-entry__title">
                  {task?.parentTitle && (
                    <span className="task-entry__parent-title">{task.parentTitle} ›{' '}</span>
                  )}
                  {/* Inner span keeps its natural width while the outer span
                      ellipsizes — measured by useCollapsedRowFit. */}
                  <span className="task-entry__title-text" data-row-fit="title-text">{displayTitle}</span>
                </span>
              )}
              {/* Seit 06.10.2026: der Unteraufgaben-Fortschritt direkt hinter dem Titel statt
                  als Balken darüber — nur zusammengeklappt, aufgeklappt steht die Checkliste selbst da. */}
              {!isExpanded && checklist.progress.total > 0 && (
                <span className="task-entry__branch-progress" data-row-fit="title-icon">
                  <BranchProgress done={checklist.progress.done} total={checklist.progress.total} />
                </span>
              )}
              {/* Stays mounted and gates internally — an unmounted component
                  cannot play its own exit animation. */}
              <CollapsedRewardIcon
                isExpanded={isExpanded}
                rewardId={task?.rewardId}
                dropdowns={dropdowns}
                onRewardChange={handleRewardChange}
                selectOnDropdownOpen={selectOnDropdownOpen}
              />
              {/* Hinter der Belohnung — derselbe Platz wie an der Unteraufgabe (#219). Die Wiederholung
                  steht seit #283 rechts in der Zeiten-Gruppe. */}
              {!isExpanded && <ReminderMark className="task-entry__comment-mark" labels={reminderLabels} onOpen={openReminders} data-row-fit="title-icon" />}
              {!isExpanded && <CommentMark className="task-entry__comment-mark" count={commentCount} onOpen={openComments} data-row-fit="title-icon" />}
              {!isExpanded && task?.projectName && projectBadgePlacement === 'title' && (
                <CollapsedProjectBadge projectName={task.projectName} projectIcon={task.projectIcon} projectIconColor={task.projectIconColor} />
              )}
              {!isExpanded && titleAccessory}
              {!twoLine && tagsNode}
            </AutoHeight>

            {/* Two-line layout: tags/duration/deadline in a second row below
                the title — the title row keeps only attention/start + title
                and stays fully readable. */}
            {twoLine && (
              <div className="task-entry__meta">
                {tagsNode}
                {timingsNode}
              </div>
            )}

            {adaptiveRow && <TagPillMeasurers tags={collapsedTagObjects} />}
            {/* Natürliche Breite des Rhythmus-Texts, unabhängig davon, ob er gerade zu sehen ist (#283). */}
            {adaptiveRow && recurrenceAlone && recurrenceDisplay === 'icon' && (
              <span className="task-entry__recurrence-measure task-entry__recurrence-mark-text" data-recurrence-measure aria-hidden="true">
                {recurrenceShort}
              </span>
            )}

            {/* Expandable content: notes + subtask */}
            {/* Close is snappier than open (0.18 s vs 0.25 s) so the notes row
                doesn't linger while the bottom-wrapper is already gone. */}
            <motion.div
              className="task-entry__extra"
              initial={initialExpanded ? false : { height: 0, opacity: 0 }}
              animate={{
                height: isExpanded ? (extraHeight ?? 'auto') : 0,
                opacity: isExpanded ? 1 : 0,
              }}
              transition={{
                height: { duration: isExpanded ? 0.25 : 0.18, ease: [0.4, 0, 0.2, 1], delay: isExpanded ? 0 : 0.02 },
                opacity: { duration: isExpanded ? 0.2 : 0.16, ease: 'easeOut' },
              }}
            >
              <div className="task-entry__extra-inner" ref={extraBodyRef}>
                {/* Row 1 holds the notes (trigger or paragraph), the subtask
                    trigger while it is still empty, and the reward — which is
                    inline only while the card is untouched and pins to the
                    trailing edge otherwise. See resolveExpandedLayout. */}
                <div className="task-entry__content-row">
                  <div className={`task-entry__notes-field task-entry__notes-field--${expandedLayout.notes}`}>
                    {showNotesIcon && (
                      <AlignLeft size={16} className="task-entry__notes-icon" aria-hidden="true" />
                    )}
                    {/* The hidden twin carries the same text in the same
                        typography, so the grid cell is exactly as wide and as
                        tall as the content — no scrollHeight read, which also
                        makes this immune to the zero-measurement trap in
                        `display: none` TabStack tabs. */}
                    <div className="task-entry__notes-sizer">
                      <span className="task-entry__notes-mirror" aria-hidden="true">
                        {notes || t('taskCreation.notesPlaceholder')}
                        {/* Keeps a trailing newline from collapsing. */}
                        {'​'}
                      </span>
                      <textarea
                        ref={notesInputRef}
                        className="task-entry__notes"
                        placeholder={t('taskCreation.notesPlaceholder')}
                        value={notes}
                        rows={1}
                        onChange={(e) => setNotes(e.target.value)}
                        onKeyDown={handleNotesKeyDown}
                        onFocus={() => setNotesFocused(true)}
                        onBlur={() => setNotesFocused(false)}
                        spellCheck={false}
                      />
                    </div>
                  </div>
                  {hasChecklist && subtaskNode}
                  {/* `layout` only — animating height here would re-measure at
                      the end of the tween and jump on the last frame.
                      Nur offen oder beim Zugehen (#303): Zugeklappt steht dieser Teil mit Höhe 0 im Baum, und ein
                      `layout`-Element misst sich bei JEDEM Rendern vor und nach dem Commit — bei ⌘A zwei erzwungene
                      Layouts je Zeile (gemessen 10.10.2026: 86 bei ~40 gebauten Zeilen). Bewegen kann sich der
                      Platz ohnehin nur, wenn die Karte offen ist. */}
                  <motion.div
                    layout={isExpanded || closing ? 'position' : false}
                    className={`task-entry__reward-slot${expandedLayout.rewardAlign === 'end' ? ' task-entry__reward-slot--end' : ''}`}
                  >
                    <TaskRewardTrigger
                      rewardId={reward}
                      onValueChange={handleRewardTriggerChange}
                      open={rewardTriggerOpen}
                      onOpenChange={setRewardTriggerOpen}
                      align={expandedLayout.rewardAlign}
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>

          {!twoLine && timingsNode}

          {/* Rechts, still, nur eingeklappt — auf **Geschafft**: erst das
              Projekt, dann die Uhrzeit, beide an der rechten Kante. */}
          {!isExpanded && task?.projectName && projectBadgePlacement === 'trailing' && (
            <span className="task-entry__trailing-project" data-row-fit="trailing-project">
              <CollapsedProjectBadge projectName={task.projectName} projectIcon={task.projectIcon} projectIconColor={task.projectIconColor} />
            </span>
          )}
          {/*
            **Die Uhrzeit steht frei nur, wenn es keine Dauer-Marke gibt.**
            Sonst liegt sie in deren Tooltip (Nutzer-Vorgabe, 2026-09-18):
            „9s von 10m · 13:53" sind drei Zahlen für eine Zeile. Ohne Marke
            gibt es nichts zu überfahren — dann bleibt sie sichtbar, statt zu
            verschwinden.
          */}
          {!isExpanded && task?.completedTime && !task.estimatedDuration && (
            <WithTooltip content={timestampTooltip(task, i18n.language, t)}>
              <span className="task-entry__completed-time" data-row-fit="completed-time">
                {task.completedTime}
              </span>
            </WithTooltip>
          )}

          {task && (
            <TaskEntryPlaySlot
              task={task}
              isExpanded={isExpanded}
              showCollapsedPlay={showCollapsedPlay}
              hidePlay={hidePlay}
              expandingFromCollapsed={expandingFromCollapsed}
              collapsingFromExpanded={collapsingFromExpanded}
              reactivateOpen={reactivateOpen}
              setReactivateOpen={setReactivateOpen}
              inProjectContext={inProjectContext}
              onDirectWake={handleDirectWake}
              onPlay={onPlay}
              onRowClick={onClick}
            />
          )}
        </div>

        {/* Bottom action bar */}
        {/* Open (total ~0.5 s — unchanged):
              – height opens  t=0      → 0.35 s
              – y slides up   t=0.05   → 0.35 s
              – opacity fades t=0.1    → 0.5 s
            Close (total ~0.25 s — snappy):
              – opacity fades t=0      → 0.22 s
              – y slides up   t=0      → 0.22 s
              – height closes t=0.03   → 0.25 s
            Close is intentionally ~half the open duration: the reveal on
            open moves only a few pixels, so mirroring that timing on close
            leaves the bar lingering visibly inside the shrinking container.
            Everything finishes together around 0.22–0.25 s so there's no
            tail where an empty box is still collapsing. */}
        <motion.div
          className="task-entry__bottom-wrapper"
          initial={initialExpanded ? false : { height: 0, opacity: 0, y: -4 }}
          animate={{
            height: isExpanded ? 'auto' : 0,
            opacity: isExpanded ? 1 : 0,
            y: isExpanded ? 0 : -20,
          }}
          transition={{
            height: { duration: isExpanded ? 0.35 : 0.22, ease: [0.4, 0, 0.2, 1], delay: isExpanded ? 0 : 0.03 },
            opacity: { duration: isExpanded ? 0.4 : 0.22, delay: isExpanded ? 0.1 : 0, ease: 'easeOut' },
            y: { duration: isExpanded ? 0.3 : 0.22, delay: isExpanded ? 0.05 : 0, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          <div className="task-entry__bottom">
            {!renderExpandedBottom ? null : (
              <ExpandedBottomBar
                showOptions={placement !== 'quick-add'}
                attention={attention}
                start={start}
                deadline={deadline}
                duration={duration}
                tags={tags}
                recurrence={recurrence}
                taskTitle={title}
                isStartToday={isStartToday}
                isStartTomorrow={isStartTomorrow}
                isStartOverdue={startDiff !== null && startDiff < 0}
                isDeadlineToday={isDeadlineToday}
                isDeadlineTomorrow={isDeadlineTomorrow}
                isDeadlineOverdue={deadlineDiff !== null && deadlineDiff < 0}
                handleAttentionChange={onAttentionByHand}
                handleStartChange={onStartByHand}
                handleDeadlineChange={onDeadlineByHand}
                handleDurationChange={onDurationByHand}
                handleTagsChange={onTagsByHand}
                // Unter einer wiederkehrenden Aufgabe gilt deren Rhythmus (#205).
                handleRecurrenceChange={task?.parentRecurring ? undefined : onRecurrenceByHand}
                playSoftRemove={playSoftRemove}
                recognizedTarget={recognitionOn ? recognizedTarget : null}
                currentLifeSpace={recognitionPlace?.lifeSpace}
                onDismissTarget={dismissTargetWords}
                pendingTagNames={recognitionOn ? pendingTagNames : undefined}
                onDismissPendingTag={dismissPendingTag}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )

  /*
    **Der Kasten bleibt stehen, auch aufgeklappt** — still, aber im Baum.

    Bis 2026-09-11 stand hier `!isExpanded`, und die Zeile wechselte beim
    Aufklappen ihre Stelle im Baum: einmal als Kind des Kontextmenüs, einmal
    nackt. React kann das nicht abgleichen, es baute die ganze Zeile neu — mit
    frischem DOM, frischen `motion`-Knoten und deshalb einer Auf-Animation, die
    auf einem eben erst entstandenen Kasten begann. Sie ruckelte genau am
    Anfang (gemeldet 2026-09-11; nachgewiesen daran, dass `.task-entry__card`
    nach dem Umschalten ein ANDERER Knoten war).

    Aufgeklappt gehört der Rechtsklick der Karte (`handleCardContextMenu`);
    dafür genügt es, den Auslöser stillzustellen.
  */
  if (task && onDelete) {
    return (
      <TaskMenu
        disabled={isExpanded}
        task={task}
        marks={getContextMenuMarks?.(task.id)}
        remindersMarked={reminderLabels.length > 0}
        {...menuHandlers}
        // Unteraufgaben sind keine Favoriten (#210) — Demnächst & Co. listen sie als eigene Zeilen.
        onFavoriteChange={isSubtask ? undefined : handleFavoriteChange}
        onFocusStart={onPlay ? () => onPlay(task.id) : undefined}
        multiSelected={multiSelected}
        onPause={handleRest}
        onReactivate={() => {
          if (inProjectContext) {
            // In-project context: skip the dropdown and write the same
            // unpause delta directly via the host's optimistic sink so
            // the row flips out of dormant state on next paint. Kept
            // silent (no toast) — the visual flip is feedback enough.
            handleDirectWake()
          } else {
            setReactivateOpen(true)
          }
        }}
        onDuplicate={() => onDuplicate?.(task.id)}
        onDelete={() => onDelete?.(task.id)}
        kind={menuKind}
        onReopen={() => (onReopen ?? onToggleComplete)?.(task.id)}
        onShowInProject={task.projectId && onShowInProject ? () => onShowInProject(task.id) : null}
      >
        {entry}
      </TaskMenu>
    )
  }

  return entry
}

// Memoized to prevent list-wide re-renders when context values tick or a sibling
// task is edited. Default shallow prop comparison is sufficient because parent
// components already pass stable callbacks (useCallback) and memoized data.
export const TaskEntry = React.memo(TaskEntryComponent)
